// React
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

// Third Party
import {
    Alert,
    alpha,
    Box,
    Chip,
    Collapse,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Button,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import { marked } from 'marked';

type GuideSubSection = {
    id: string;
    title: string;
    level: number;
    indexLabel: string;
};

type GuideSection = {
    id: string;
    title: string;
    order: number;
    filePath: string;
    markdownContent: string;
    subSections: GuideSubSection[];
};

export type ModuleMarkdownGuideProps = {
    /** Vite `import.meta.glob` map for raw chapter `.md` files. */
    chapterMarkdownFiles: Record<string, () => Promise<unknown>>;
    /** Public URL base for chapter files, e.g. `/resources/docs/selective-sync-guide/chapters` (no trailing slash). */
    chaptersPublicBasePath: string;
    /** Title shown in the in-page guide sidebar header. */
    guideDisplayTitle: string;
};

const createSlug = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/<[^>]*>/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

const parseFileNameFromPath = (filePath: string) => {
    const parts = filePath.split('/');
    return parts[parts.length - 1] ?? '';
};

const parseOrderFromFileName = (fileName: string) => {
    const match = /^(\d+)/.exec(fileName);
    return match ? Number.parseInt(match[1], 10) : Number.NaN;
};

const parseChapterMarkdown = (markdownContent: string, chapterOrder: number) => {
    const lines = markdownContent.split(/\r?\n/);

    let chapterTitle = '';
    const subSections: GuideSubSection[] = [];
    let h2Index = 0;
    let h3Index = 0;

    for (const line of lines) {
        if (!chapterTitle && line.startsWith('# ')) {
            chapterTitle = line.replace(/^#\s+/, '').trim();
            continue;
        }

        const subSectionMatch = /^(##|###)\s+(.+)$/.exec(line.trim());
        if (!subSectionMatch) continue;

        const headingLevel = subSectionMatch[1] === '##' ? 2 : 3;
        const headingTitle = subSectionMatch[2].trim();
        if (!headingTitle) continue;

        if (headingLevel === 2) {
            h2Index += 1;
            h3Index = 0;
        } else {
            h3Index += 1;
        }

        const indexLabel = headingLevel === 2 ? `${chapterOrder}.${h2Index}` : `${chapterOrder}.${h2Index}.${h3Index}`;
        const id = `s-${indexLabel.replace(/\./g, '-')}-${createSlug(headingTitle)}`;

        subSections.push({
            id,
            title: headingTitle,
            level: headingLevel,
            indexLabel,
        });
    }

    return {
        chapterTitle: chapterTitle || `Chapter ${chapterOrder}`,
        subSections,
    };
};

const buildRenderableMarkdown = (section: GuideSection, chaptersImagesPublicPrefix: string) => {
    const subSectionIdMap = new Map<string, string>();
    for (const subSection of section.subSections) {
        subSectionIdMap.set(subSection.title.toLowerCase(), subSection.id);
    }

    const withAnchors = section.markdownContent
        .split(/\r?\n/)
        .map((line) => {
            const subSectionMatch = /^(##|###)\s+(.+)$/.exec(line.trim());
            if (!subSectionMatch) return line;

            const headingTitle = subSectionMatch[2].trim();
            const id = subSectionIdMap.get(headingTitle.toLowerCase());
            if (!id) return line;

            return `${subSectionMatch[1]} <span id="${id}"></span>${headingTitle}`;
        })
        .join('\n');

    const escapedPrefix = chaptersImagesPublicPrefix.replace(/\$/g, '$$$$');
    return withAnchors.replace(/!\[([^\]]*)\]\(\.\/images\/([^)]+)\)/gim, `![$1](${escapedPrefix}$2)`);
};

const renderHighlightedText = (value: string, searchValue: string) => {
    const normalizedSearchValue = searchValue.trim();
    if (!normalizedSearchValue) return value;

    const escapedSearchValue = normalizedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matcher = new RegExp(`(${escapedSearchValue})`, 'ig');
    const segments = value.split(matcher);

    return segments.map((segment, index) => {
        const isMatch = segment.toLowerCase() === normalizedSearchValue.toLowerCase();
        if (!isMatch) return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
        return (
            <Box
                key={`match-${index}`}
                component="mark"
                sx={{
                    backgroundColor: '#fff59d',
                    color: 'inherit',
                    px: 0.3,
                    borderRadius: 0.5,
                }}>
                {segment}
            </Box>
        );
    });
};

const SIDEBAR_WIDTH = 320;

export const ModuleMarkdownGuide: React.FC<ModuleMarkdownGuideProps> = ({ chapterMarkdownFiles, chaptersPublicBasePath, guideDisplayTitle }) => {
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const contentReference = React.useRef<HTMLDivElement>(null);
    const [loading, setLoading] = React.useState(true);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [sections, setSections] = React.useState<GuideSection[]>([]);
    const [activeSectionId, setActiveSectionId] = React.useState('');
    const [expandedSectionId, setExpandedSectionId] = React.useState('');
    const [searchText, setSearchText] = React.useState('');
    const [activeSectionHtml, setActiveSectionHtml] = React.useState('');

    const chaptersImagesPublicPrefix = React.useMemo(
        () => `${chaptersPublicBasePath.replace(/\/$/, '')}/images/`,
        [chaptersPublicBasePath]
    );

    React.useEffect(() => {
        const loadGuide = async () => {
            try {
                setLoading(true);
                const fileEntries = Object.entries(chapterMarkdownFiles)
                    .map(([absolutePath, loader]) => ({
                        fileName: parseFileNameFromPath(absolutePath),
                        loader,
                    }))
                    .filter((entry) => entry.fileName.toLowerCase().endsWith('.md'))
                    .sort((left, right) => {
                        const leftOrder = parseOrderFromFileName(left.fileName);
                        const rightOrder = parseOrderFromFileName(right.fileName);
                        const leftHasOrder = Number.isFinite(leftOrder);
                        const rightHasOrder = Number.isFinite(rightOrder);

                        if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) {
                            return leftOrder - rightOrder;
                        }
                        if (leftHasOrder !== rightHasOrder) {
                            return leftHasOrder ? -1 : 1;
                        }
                        return left.fileName.localeCompare(right.fileName, undefined, { numeric: true, sensitivity: 'base' });
                    });

                if (fileEntries.length === 0) {
                    setSections([]);
                    setErrorMessage('No guide chapters were found for this module.');
                    return;
                }

                const basePath = chaptersPublicBasePath.replace(/\/$/, '');

                const loadedSections = await Promise.all(
                    fileEntries.map(async ({ fileName, loader }, index) => {
                        const markdownContent = String(await loader());
                        const chapterOrder = index + 1;
                        const parsed = parseChapterMarkdown(markdownContent, chapterOrder);
                        return {
                            id: fileName.replace(/\.md$/i, ''),
                            title: parsed.chapterTitle,
                            order: chapterOrder,
                            filePath: `${basePath}/${fileName}`,
                            markdownContent,
                            subSections: parsed.subSections,
                        };
                    })
                );

                const renderer = new marked.Renderer();
                renderer.link = ({ href, title, tokens }) => {
                    const text = marked.Parser.parseInline(tokens);
                    const safeHref = href ?? '#';
                    const titleAttribute = title ? ` title="${title}"` : '';
                    return `<a href="${safeHref}"${titleAttribute} target="_blank" rel="noopener noreferrer">${text}</a>`;
                };

                marked.setOptions({
                    gfm: true,
                    breaks: false,
                    renderer,
                });

                setSections(loadedSections);
                setExpandedSectionId(loadedSections[0]?.id ?? '');
                setErrorMessage(null);
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : 'Failed to load guide.');
            } finally {
                setLoading(false);
            }
        };

        void loadGuide();
    }, [chapterMarkdownFiles, chaptersPublicBasePath]);

    React.useEffect(() => {
        const activeSection = sections.find((section) => section.id === activeSectionId);
        if (!activeSection) {
            setActiveSectionHtml('');
            return;
        }

        const renderableMarkdown = buildRenderableMarkdown(activeSection, chaptersImagesPublicPrefix);
        const html = marked.parse(renderableMarkdown);
        const htmlValue = typeof html === 'string' ? html : String(html);
        setActiveSectionHtml(htmlValue);
        if (contentReference.current) {
            contentReference.current.scrollTop = 0;
        }
    }, [activeSectionId, chaptersImagesPublicPrefix, sections]);

    React.useEffect(() => {
        if (sections.length === 0) return;

        const sectionFromUrl = searchParams.get('section')?.trim() ?? '';
        const foundSection = sections.find((section) => section.id === sectionFromUrl);
        const defaultSectionId = sections[0]?.id ?? '';

        if (foundSection) {
            if (activeSectionId !== foundSection.id) {
                setActiveSectionId(foundSection.id);
                setExpandedSectionId(foundSection.id);
            }
            return;
        }

        if (defaultSectionId) {
            setActiveSectionId(defaultSectionId);
            setExpandedSectionId(defaultSectionId);
            setSearchParams({ section: defaultSectionId }, { replace: true });
        }
    }, [activeSectionId, searchParams, sections, setSearchParams]);

    const goToSection = React.useCallback(
        (id: string) => {
            if (!id || id === activeSectionId) return;
            setActiveSectionId(id);
            setExpandedSectionId(id);
            setSearchParams({ section: id });
        },
        [activeSectionId, setSearchParams]
    );

    const toggleSectionExpansion = React.useCallback((sectionId: string) => {
        setExpandedSectionId((currentValue) => (currentValue === sectionId ? '' : sectionId));
    }, []);

    const scrollToSubSection = React.useCallback((subSectionId: string) => {
        if (!contentReference.current) return;
        const target = contentReference.current.querySelector<HTMLElement>(`#${CSS.escape(subSectionId)}`);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const filteredSections = React.useMemo(() => {
        const searchValue = searchText.trim().toLowerCase();
        if (!searchValue) return sections;

        return sections.filter((section) => {
            if (section.title.toLowerCase().includes(searchValue)) return true;
            return section.subSections.some((subSection) => subSection.title.toLowerCase().includes(searchValue));
        });
    }, [searchText, sections]);

    React.useEffect(() => {
        const searchValue = searchText.trim().toLowerCase();
        if (!searchValue) return;

        const matchingSubSectionParent = sections.find((section) =>
            section.subSections.some((subSection) => subSection.title.toLowerCase().includes(searchValue))
        );

        if (matchingSubSectionParent && expandedSectionId !== matchingSubSectionParent.id) {
            setExpandedSectionId(matchingSubSectionParent.id);
        }
    }, [expandedSectionId, searchText, sections]);

    const activeSectionIndex = React.useMemo(() => {
        return sections.findIndex((section) => section.id === activeSectionId);
    }, [activeSectionId, sections]);

    const previousSection = activeSectionIndex > 0 ? sections[activeSectionIndex - 1] : null;
    const nextSection = activeSectionIndex >= 0 && activeSectionIndex < sections.length - 1 ? sections[activeSectionIndex + 1] : null;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (errorMessage) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{errorMessage}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 16px)', minHeight: 0, overflow: 'hidden' }}>
            <Paper
                elevation={0}
                sx={{
                    width: { xs: '100%', md: SIDEBAR_WIDTH },
                    minWidth: { xs: '100%', md: SIDEBAR_WIDTH },
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'stretch',
                    maxHeight: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderRadius: 0,
                    bgcolor: theme.palette.common.white,
                }}>
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <MenuBookRoundedIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: -0.2, color: 'text.primary' }}>
                            {guideDisplayTitle}
                        </Typography>
                        <Chip size="small" label={`${sections.length} chapters`} sx={{ ml: 'auto', height: 22, fontSize: '0.7rem', fontWeight: 600 }} />
                    </Box>
                    <TextField
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder="Search chapters & sections..."
                        size="small"
                        fullWidth
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '0.85rem',
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon sx={{ color: 'text.disabled' }} fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchText('')} aria-label="Clear search">
                                            <ClearRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            },
                        }}
                    />
                </Box>

                <Divider />

                <Box sx={{ overflow: 'auto', flex: 1 }}>
                    <List disablePadding sx={{ py: 0.5 }}>
                        {filteredSections.map((section) => {
                            const isActive = activeSectionId === section.id;
                            const isExpanded = expandedSectionId === section.id;
                            const hasSubSections = section.subSections.length > 0;

                            return (
                                <React.Fragment key={section.id}>
                                    <ListItemButton
                                        selected={isActive}
                                        onClick={() => goToSection(section.id)}
                                        sx={{
                                            mx: 1,
                                            mb: 0.3,
                                            borderRadius: 2,
                                            py: 0.75,
                                            px: 1.5,
                                            transition: 'all 0.15s ease',
                                            '&.Mui-selected': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.24),
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.3),
                                                },
                                            },
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            },
                                        }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isActive ? 'primary.dark' : 'text.primary',
                                                    fontSize: '0.78rem',
                                                    width: 20,
                                                    textAlign: 'center',
                                                }}>
                                                {section.order}
                                            </Typography>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={renderHighlightedText(section.title, searchText)}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.9rem',
                                                    fontWeight: isActive ? 700 : 500,
                                                    color: isActive ? 'primary.dark' : 'text.primary',
                                                    lineHeight: 1.4,
                                                },
                                            }}
                                        />
                                        {hasSubSections && (
                                            <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        toggleSectionExpansion(section.id);
                                                    }}
                                                    sx={{ ml: 0.5, p: 0.3 }}>
                                                    {isExpanded ? (
                                                        <ExpandLessRoundedIcon sx={{ fontSize: '1.1rem' }} />
                                                    ) : (
                                                        <ExpandMoreRoundedIcon sx={{ fontSize: '1.1rem' }} />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </ListItemButton>

                                    <Collapse in={isExpanded && hasSubSections} timeout="auto" unmountOnExit>
                                        <List disablePadding sx={{ ml: 2, mr: 1, mb: 0.5 }}>
                                            {section.subSections.map((subSection) => (
                                                <ListItemButton
                                                    key={subSection.id}
                                                    onClick={() => {
                                                        if (activeSectionId !== section.id) {
                                                            goToSection(section.id);
                                                            return;
                                                        }
                                                        scrollToSubSection(subSection.id);
                                                    }}
                                                    sx={{
                                                        pl: subSection.level === 2 ? 2.5 : 4,
                                                        py: 0.35,
                                                        borderRadius: 1.5,
                                                        minHeight: 30,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.action.hover, 0.5),
                                                        },
                                                    }}>
                                                    <ListItemIcon sx={{ minWidth: 16 }}>
                                                        <CircleRoundedIcon sx={{ fontSize: 5, color: 'text.disabled' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <React.Fragment>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ fontSize: '0.74rem', color: 'text.primary', mr: 0.6, fontWeight: 700 }}>
                                                                    {subSection.indexLabel}
                                                                </Typography>
                                                                {renderHighlightedText(subSection.title, searchText)}
                                                            </React.Fragment>
                                                        }
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                fontSize: '0.82rem',
                                                                color: 'text.primary',
                                                                fontWeight: 500,
                                                                lineHeight: 1.35,
                                                            },
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Collapse>
                                </React.Fragment>
                            );
                        })}
                        {filteredSections.length === 0 && (
                            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.disabled">
                                    No matching sections found.
                                </Typography>
                            </Box>
                        )}
                    </List>
                </Box>
            </Paper>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}>
                <Box
                    ref={contentReference}
                    className="brand-scrollbar"
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        minHeight: 0,
                        width: '100%',
                        px: { xs: 1.5, md: 3 },
                        pt: { xs: 1.5, md: 2 },
                        pb: { xs: 2, md: 3 },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                            my: 2,
                            mx: 'auto',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                        },
                        '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            my: 2,
                        },
                        '& th, & td': {
                            border: `1px solid ${theme.palette.divider}`,
                            padding: theme.spacing(1),
                            verticalAlign: 'top',
                        },
                        '& p, & li': {
                            fontSize: '0.95rem',
                            lineHeight: 1.8,
                        },
                        '& h1, & h2, & h3, & h4': {
                            scrollMarginTop: '1rem',
                            lineHeight: 1.3,
                        },
                        '& h1': { fontSize: '1.75rem', fontWeight: 700, mt: 1, mb: 1.5 },
                        '& h2': {
                            fontSize: '1.4rem',
                            fontWeight: 600,
                            mt: 3.5,
                            mb: 1,
                            pb: 0.6,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2.5, mb: 0.8 },
                        '& h4': { fontSize: '1rem', fontWeight: 600, mt: 2, mb: 0.5 },
                        '& a': { color: 'primary.main' },
                        '& blockquote': {
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            mx: 0,
                            pl: 2,
                            color: 'text.secondary',
                        },
                        '& pre': {
                            bgcolor: alpha(theme.palette.grey[900], 0.9),
                            color: theme.palette.common.white,
                            p: 2,
                            borderRadius: 2,
                            overflowX: 'auto',
                        },
                        '& code': {
                            fontFamily: 'monospace',
                            fontSize: '0.88em',
                        },
                        '& hr': {
                            border: 'none',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            my: 3,
                        },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Chip
                            size="small"
                            label={`Chapter ${Math.max(activeSectionIndex + 1, 1)} of ${sections.length}`}
                            color="primary"
                            variant="outlined"
                            sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    </Box>

                    <Box dangerouslySetInnerHTML={{ __html: activeSectionHtml }} />
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        width: '100%',
                        px: { xs: 1.5, md: 3 },
                        py: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<NavigateBeforeRoundedIcon />}
                            disabled={!previousSection}
                            onClick={() => previousSection && goToSection(previousSection.id)}
                            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2, px: 2 }}>
                            {previousSection?.title ?? 'Previous'}
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            endIcon={<NavigateNextRoundedIcon />}
                            disabled={!nextSection}
                            onClick={() => nextSection && goToSection(nextSection.id)}
                            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2, px: 2 }}>
                            {nextSection?.title ?? 'Next'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ModuleMarkdownGuide;
